/* ══════════════════════════════════════════════════════════════
   Key Levels — Support/Resistance with Visual Chart
   Shows key price levels with proximity indicators
   ══════════════════════════════════════════════════════════════ */

import { fetchMLStrategy } from '../services/ml-api';
import { fetchIndices } from '../services/market-data';
import { apiFetch } from '../services/api-client';
import { renderUnavailableBlock } from './data-state';

export interface KeyLevelsData {
  currentPrice: number;
  support: Array<{ price: number; strength: 'strong' | 'moderate' | 'weak' }>;
  resistance: Array<{ price: number; strength: 'strong' | 'moderate' | 'weak' }>;
  maxPain: number;
  pivotPoint: number;
}

function computeLevels(currentPrice: number, entry?: number, target?: number, stopLoss?: number): KeyLevelsData {
  // Compute pivot-based support and resistance levels
  const pivotPoint = entry || currentPrice;
  const range = currentPrice * 0.01; // 1% range as base

  const support: Array<{ price: number; strength: 'strong' | 'moderate' | 'weak' }> = [];
  const resistance: Array<{ price: number; strength: 'strong' | 'moderate' | 'weak' }> = [];

  if (stopLoss && stopLoss > 0) {
    support.push({ price: stopLoss, strength: 'strong' });
  }
  support.push({ price: Math.round(currentPrice - range), strength: stopLoss ? 'moderate' : 'strong' });
  support.push({ price: Math.round(currentPrice - range * 2), strength: 'weak' });

  if (target && target > 0) {
    resistance.push({ price: target, strength: 'strong' });
  }
  resistance.push({ price: Math.round(currentPrice + range), strength: target ? 'moderate' : 'strong' });
  resistance.push({ price: Math.round(currentPrice + range * 2), strength: 'weak' });

  // Sort support descending, resistance ascending
  support.sort((a, b) => b.price - a.price);
  resistance.sort((a, b) => a.price - b.price);

  return {
    currentPrice,
    support: support.slice(0, 3),
    resistance: resistance.slice(0, 3),
    maxPain: Math.round(currentPrice - range * 0.5),
    pivotPoint: Math.round(pivotPoint),
  };
}

function renderWithData(container: HTMLElement, d: KeyLevelsData): void {
  const allPrices = [...d.support.map(s => s.price), d.currentPrice, ...d.resistance.map(r => r.price)];
  const minP = Math.min(...allPrices);
  const maxP = Math.max(...allPrices);
  const range = maxP - minP || 1;
  const pct = (price: number) => ((price - minP) / range * 100).toFixed(1);

  const strengthWidth: Record<string, string> = { strong: '100%', moderate: '66%', weak: '33%' };
  const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;

  container.innerHTML = `
    <div class="glass-card key-levels-panel">
      <div class="card-header">
        <h3>📏 Key Levels</h3>
        <span class="current-price-badge">CMP: ${fmt(d.currentPrice)}</span>
      </div>

      <div class="levels-visual">
        <div class="level-line-container">
          ${d.resistance.map(r => `
            <div class="level-marker resistance" style="bottom:${pct(r.price)}%">
              <span class="level-price">${fmt(r.price)}</span>
              <div class="level-bar" style="width:${strengthWidth[r.strength]}"></div>
              <span class="level-strength">${r.strength}</span>
            </div>`).join('')}
          <div class="level-marker current" style="bottom:${pct(d.currentPrice)}%">
            <span class="level-price current-label">▶ ${fmt(d.currentPrice)}</span>
          </div>
          ${d.support.map(s => `
            <div class="level-marker support" style="bottom:${pct(s.price)}%">
              <span class="level-price">${fmt(s.price)}</span>
              <div class="level-bar" style="width:${strengthWidth[s.strength]}"></div>
              <span class="level-strength">${s.strength}</span>
            </div>`).join('')}
        </div>
      </div>

      <div class="levels-extra">
        <div class="extra-level"><span>Max Pain</span><span>${fmt(d.maxPain)}</span></div>
        <div class="extra-level"><span>Pivot Point</span><span>${fmt(d.pivotPoint)}</span></div>
        <div class="extra-level"><span>Nearest Support</span><span>${fmt(d.support[0].price)} (${((d.currentPrice - d.support[0].price) / d.currentPrice * 100).toFixed(1)}% away)</span></div>
        <div class="extra-level"><span>Nearest Resistance</span><span>${fmt(d.resistance[0].price)} (${((d.resistance[0].price - d.currentPrice) / d.currentPrice * 100).toFixed(1)}% away)</span></div>
      </div>
    </div>`;
}

export function renderKeyLevels(container: HTMLElement, data?: KeyLevelsData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div class="glass-card key-levels-panel" style="padding:2rem;text-align:center;">Loading key levels...</div>`;

    // Try dedicated key-levels endpoint first, then compute from strategy + indices
    apiFetch<KeyLevelsData>('/ml/key-levels', { cacheTTL: 60 }).then(result => {
      if (result.data && result.data.currentPrice) {
        renderWithData(container, result.data);
      } else {
        throw new Error('No key levels data');
      }
    }).catch(() => {
      // Fallback: compute from ML strategy + live Nifty price
      Promise.all([fetchMLStrategy(), fetchIndices()]).then(([strat, indices]) => {
        const nifty = indices.find(idx =>
          idx.name?.toUpperCase().includes('NIFTY 50') || idx.symbol?.toUpperCase() === 'NIFTY 50'
        );
        const currentPrice = nifty?.last || 0;

        if (currentPrice > 0 && strat) {
          renderWithData(container, computeLevels(currentPrice, strat.entry?.min, strat.target?.min, strat.stopLoss));
        } else if (currentPrice > 0) {
          renderWithData(container, computeLevels(currentPrice));
        } else {
          container.innerHTML = renderUnavailableBlock('glass-card key-levels-panel', 'Live key levels unavailable');
        }
      }).catch(() => {
        container.innerHTML = renderUnavailableBlock('glass-card key-levels-panel', 'Live key levels unavailable');
      });
    });
  }
}
